import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Gift, Copy, Check, Users, Sparkles, Trophy } from "lucide-react";

const statusStyle = (s) =>
  s === "rewarded" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
  : s === "subscribed" ? "text-amber-700 bg-amber-50 border-amber-200"
  : "text-slate-500 bg-slate-50 border-slate-200";
const statusLabel = (s) => (s === "rewarded" ? "Subscribed · reward applied" : s === "subscribed" ? "Subscribed" : "Signed up");

function FounderKickerProgress({ status }) {
  if (!status?.is_founder) return null;
  const paid = status.paid_referrals || 0;
  const threshold = status.kicker_threshold || 3;
  const kicked = status.kicker_applied;
  const pct = kicked ? 100 : Math.min(100, Math.round((paid / threshold) * 100));
  return (
    <div data-testid="founder-kicker-progress" className="rounded-2xl border-2 border-lime-400/60 bg-gradient-to-br from-emerald-950 to-emerald-900 text-white p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-lime-300">
            <Trophy className="h-3.5 w-3.5" /> Founder #{String(status.founder_number).padStart(3, "0")} · Referral kicker
          </div>
          <h3 className="mt-2 font-heading text-xl font-bold">
            {kicked
              ? <>Kicker unlocked — your price is <span className="text-lime-400">${status.kicker_monthly}/mo forever</span>.</>
              : <>Refer <span className="text-lime-400">{Math.max(0, threshold - paid)}</span> more paid sub{threshold - paid === 1 ? "" : "s"} to drop from <span className="line-through text-emerald-100/50">$39</span> <span className="text-lime-400">$29</span>/mo forever.</>}
          </h3>
        </div>
        <div className="text-center min-w-[110px]">
          <p className="font-data text-3xl font-semibold">{paid}<span className="text-emerald-100/50 text-lg">/{threshold}</span></p>
          <p className="text-[11px] text-emerald-100/80">paid referrals</p>
        </div>
      </div>
      <div className="mt-4 h-2 rounded-full bg-emerald-800/60 overflow-hidden">
        <div className={`h-full transition-all ${kicked ? "bg-lime-400" : "bg-gradient-to-r from-lime-400 to-emerald-400"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function Referrals() {
  const [data, setData] = useState(null);
  const [founder, setFounder] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referrals/me").then((r) => setData(r.data)).catch(() => {});
    api.get("/founding/me").then((r) => setFounder(r.data)).catch(() => {});
  }, []);

  const link = data ? `https://bidmatchai.io/?ref=${data.code}` : "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); toast.success("Referral link copied"); setTimeout(() => setCopied(false), 2000); }
    catch (e) { toast.error("Could not copy"); }
  };

  if (!data) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 border-[3px] border-slate-200 border-t-slate-900 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="referrals-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Refer &amp; Earn</h1>
        <p className="text-slate-500 mt-1">Invite a contractor. When they subscribe to a paid plan, you get <span className="font-semibold text-emerald-700">1 month free</span> — automatically credited to your next invoice.</p>
      </div>

      <FounderKickerProgress status={founder} />

      <div className="bg-emerald-900 text-white rounded-2xl p-7" data-testid="referral-link-card">
        <div className="flex items-center gap-2 text-lime-300 text-sm font-semibold uppercase tracking-wider"><Gift className="h-4 w-4" /> Your referral link</div>
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <input readOnly value={link} data-testid="referral-link-input" className="flex-1 bg-white/10 border border-white/20 rounded-md px-4 py-3 text-sm text-white font-data" />
          <button onClick={copy} data-testid="copy-referral-button" className="bg-white text-emerald-900 rounded-md px-5 py-3 text-sm font-semibold hover:bg-slate-100 transition-colors inline-flex items-center justify-center gap-2">
            {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy link</>}
          </button>
        </div>
        <p className="text-slate-300 text-sm mt-3">Share it by email, text, or LinkedIn. Your code: <span className="font-data text-white">{data.code}</span></p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Stat icon={Users} label="People referred" value={data.stats.signed_up} />
        <Stat icon={Sparkles} label="Converted to paid" value={data.stats.rewarded} />
        <Stat icon={Gift} label="Free months earned" value={data.stats.free_months_earned} accent />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
        <h3 className="font-heading text-lg font-semibold mb-4">Your referrals</h3>
        {data.referrals.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="referrals-empty">No referrals yet. Share your link above to start earning free months.</p>
        ) : (
          <div className="space-y-2" data-testid="referrals-list">
            {data.referrals.map((r, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`referral-row-${i}`}>
                <span className="text-sm text-slate-700 truncate">{r.email}</span>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${statusStyle(r.status)}`}>{statusLabel(r.status)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
    <Icon className={`h-5 w-5 ${accent ? "text-emerald-600" : "text-slate-400"}`} />
    <p className={`font-heading text-3xl font-bold mt-2 ${accent ? "text-emerald-700" : "text-slate-900"}`}>{value}</p>
    <p className="text-sm text-slate-500 mt-0.5">{label}</p>
  </div>
);
